import Tracker from "./Tracker";
import { ErrorObject } from "./utils/createUnknownErrorObject";
import { timeoutPromise } from "./utils/timeoutPromise";

const RETRIES = 3;
const DELAY_MS = 500;
const DEFAULT_CONNECTION_TIMEOUT_MS = 180_000;
const IDEMPOTENCY_PARAMETER_NAME = "shift4js-idempotency-key";

export type HttpCaller<T> = (url: URL | RequestInfo, request: T, timeout: number, tracker?: Tracker) => Promise<Response | { error: ErrorObject }>;

function generateIdempotencyKey() {
    const timestamp = Date.now();
    const randomPart = getRandomPart();
    return `${timestamp}-${randomPart}`

    function getRandomPart() {
        return (Math.floor(Math.random() * (9999999998)) + 1)
            .toString()
            .padStart(10, '0')
    }
}

function returnNewUrlWithAddedIdempotencyParam(url: RequestInfo | URL) {
    let parsedUrl: URL;

    if (typeof url === 'string' || url instanceof URL) {
        parsedUrl = new URL(url.toString());
    } else if (url instanceof Request) {
        parsedUrl = new URL(url.url);
    } else {
        throw new Error('Unsupported fetch input type');
    }

    if (parsedUrl.searchParams.has(IDEMPOTENCY_PARAMETER_NAME)) {
        return parsedUrl;
    }

    parsedUrl.searchParams.set(IDEMPOTENCY_PARAMETER_NAME, generateIdempotencyKey());
    return parsedUrl;
}

export async function callWithIdempotentRetry<T>(
    url: Request | string | URL,
    httpCaller: HttpCaller<T>,
    callerName: string,
    request?: T,
    tracker?: Tracker,
    timeoutInMs?: number
) {
    const urlWithIdempotencyParam = returnNewUrlWithAddedIdempotencyParam(url);
    const startTimeMs = new Date().getTime();
    const connectionTimeoutMs = timeoutInMs || DEFAULT_CONNECTION_TIMEOUT_MS;

    for (let attempt = 0; attempt <= RETRIES; attempt++) {
        try {
            const connectionTimeoutLeftMs = connectionTimeoutMs - (new Date().getTime() - startTimeMs);

            const response = await httpCaller(urlWithIdempotencyParam, request, connectionTimeoutLeftMs, tracker);

            const timeoutExceeded = connectionTimeoutExceeded();
            if (timeoutExceeded || attemptsExceeded(attempt) || isProperResponse(response)) {
                trackAttempts(attempt, timeoutExceeded);
                return response;
            }

            await timeoutPromise(DELAY_MS);
        } catch (err) {
            const timeoutExceeded = connectionTimeoutExceeded();
            if (attemptsExceeded(attempt) || timeoutExceeded) {
                trackAttempts(attempt, timeoutExceeded);
                throw err;
            } else {
                await timeoutPromise(DELAY_MS);
            }
        }
    }

    function isProperResponse(response: Response | { error: ErrorObject; }) {
        return response instanceof Response && response.status < 500;
    }

    function attemptsExceeded(attempt: number) {
        return attempt >= RETRIES;
    }

    function connectionTimeoutExceeded() {
        return new Date().getTime() - startTimeMs > connectionTimeoutMs;
    }

    function trackAttempts(attempt: number, timeoutExceeded: boolean = false) {
        if (tracker && (attempt !== 0 || timeoutExceeded)) {
            tracker.track(`${callerName}.retried`, { url: url, retryAttempts: attempt, timeoutExceeded: timeoutExceeded })
        }
    }
}